<template>
  <div>
    <treeselect
      id="users-treeselect"
      :options="options"
      :placeholder="placeholder"
      :multiple="multiple"
      v-model="user_ids"
      :disabled="disabled || busy"
      :clearable="showClear"
      @open="onOpen"
      @close="onClose"
      @input="onInput"
      @select="onSelect"
    >
      <div v-if="showSelectAll && multiple" slot="before-list">
        <b-row class="border-bottom">
          <b-col class="font-italic">
            <a class="ml-2" href="#" @click.prevent="onTreeSelectAll()">
              <small>{{ trans('select_all') }}</small>
            </a>
            <a v-if="showPermission" class="ml-2" href="#" @click.prevent="onTreeSelectAll('staff')">
              <small>{{ trans('permission_staff') }}</small>
            </a>
            <a v-if="showPermission" class="ml-2" href="#" @click.prevent="onTreeSelectAll('staff_no_messaging')">
              <small>{{ trans('staff_no_messaging') }}</small>
            </a>
            <a v-if="showPermission" class="ml-2" href="#" @click.prevent="onTreeSelectAll('guest')">
              <small>{{ trans('permission_guest') }}</small>
            </a>
            <a v-if="showPermission" class="ml-2" href="#" @click.prevent="onTreeSelectAll('communication_agent')">
              <small>{{ trans('permission_communication_agent') }}</small>
            </a>
            <a v-if="showPermission" class="ml-2" href="#" @click.prevent="onTreeSelectAll('attendant')">
              <small>{{ trans('permission_attendant') }}</small>
            </a>
            <a v-if="showPermission" class="ml-2" href="#" @click.prevent="onTreeSelectAll('admin')">
              <small>{{ trans('permission_admin') }}</small>
            </a>
            <a v-if="showPermission" class="ml-2" href="#" @click.prevent="onTreeSelectAll('owner')">
              <small>{{ trans('permission_owner') }}</small>
            </a>
          </b-col>
          <b-col class="col-auto font-italic">
            <a class="mr-2" href="#" @click.prevent="onTreeClearAll()">
              <small>{{ trans('unselect_all') }}</small>
            </a>
          </b-col>
        </b-row>
      </div>
      <label slot="option-label" slot-scope="{ node }" v-html="buildOptionHtmlLabel(node.raw)">
      </label>
      <span slot="value-label" slot-scope="{ node }" :title="node.raw.optionLabel" v-b-tooltip>
        {{ node.raw.name || (showEmail ? (node.raw.email || node.raw.mobile) : (node.raw.mobile || node.raw.email)) }}
      </span>
      <div v-if="showAddGuest" slot="after-list" class="text-right">
        <b-button variant="link"class="pl-0" @click="onClickAdd" >
          <small>
            <i class="fa fa-fw fa-plus"></i>
            {{ trans('add') }}
          </small>
        </b-button>
      </div>
    </treeselect>
    <add-guest-modal
      v-if="showAddGuest"
      :organization="organization"
      :initial="query"
      :is-mobile-required="showMobile && !showEmail"
      @added="onAdded"
    ></add-guest-modal>
  </div>
</template>

<style>
  #users-treeselect .vue-treeselect__multi-value-label {
    white-space: normal;
  }
</style>

<script>
  import Treeselect from '@riophae/vue-treeselect'
  import AddGuestModal from './AddGuestModal'

  export default {
    props: {
      organization: Object,
      value: [Array, Number], // array of user ids or a single user id when multiple is false
      emails: Array, // Only use with multiple
      multiple: {
        type: Boolean,
        default: true,
      },
      confirmed: {
        type: Boolean,
        default: true, // true, false or null
      },
      placeholder: {
        type: String,
        default: trans('user'),
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      showEmail: {
        type: Boolean,
        default: false,
      },
      showMobile: {
        type: Boolean,
        default: false,
      },
      showPermission: {
        type: Boolean,
        default: false,
      },
      showAddGuest: {
        type: Boolean,
        default: false,
      },
      permission: String, // staff, staff_no_messaging, owner, admin, communication_agent, guest or attendant
      showSelectAll: { // only available for multiple
        type: Boolean,
        default: false,
      },
      showClear: {
        type: Boolean,
        default: true,
      },
    },
    components: {
      Treeselect,
      AddGuestModal,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        users: null,
        user_ids: _.clone(this.value) || null,
        user_emails: null,
        opened: false,
        query: null,
      }
    },
    watch: {
      value() {
        if (!_.isEqual(this.value, this.user_ids))
          this.user_ids = _.clone(this.value)
      },
    },
    computed: {
      options() {
        if (!this.users) return _.map(this.multiple ? this.user_ids : [this.user_ids], id => {
          return {
            id: id,
            label: '...',
            optionLabel: '...',
            name: '...',
          }
        })
        return (this.users).reduce((nodes, user) => {
          if (this.showEmail && !this.showMobile && !user.email ||
            this.showMobile && !this.showEmail && !user.mobile)
            return nodes
          let label = user.name || user.email
          let optionLabel = label
          let extra = []
          if (user.permission) extra.push(trans('permission_'+user.permission))
          if (user.email) extra.push(user.email)
          if (user.mobile) extra.push(user.mobile)
          if (extra.length) optionLabel += ` <${extra.join(' ')}>`
          nodes.push({
            id: user.id,
            label: label,
            optionLabel: optionLabel,
            name: user.name,
            email: user.email,
            mobile: user.mobile,
            permission: user.permission,
          })
          return nodes
        }, [])
      },
    },
    methods: {
      onTreeSelectAll(permission) {
        if (!permission) {
          this.user_ids = _.map(this.users, 'id')
        } else {
          this.user_ids = _.reduce(this.users, (ids, user) => {
            if ((this.user_ids || []).indexOf(user.id) >= 0 ||
              user.permission == permission)
              ids.push(user.id)
            return ids
          }, [])
        }
        this.emitIfChanged()
      },
      onTreeClearAll(index) {
        this.user_ids = []
        this.emitIfChanged()
      },
      buildOptionHtmlLabel(recipient) {
        let parts = []
        if (recipient.name) parts.push(`<span>${recipient.name}</span>`)
        if (recipient.permission && this.showPermission) {
          parts.push(`<span class="badge badge-secondary">${trans('permission_'+recipient.permission)}</span>`)
        }
        if (recipient.email) {
          if (this.showEmail && !this.showMobile)
            parts.push(`<strong>${recipient.email}</strong>`)
          else
            parts.push(`<span>${recipient.email}</span>`)
        }
        if (recipient.mobile) {
          if (this.showMobile && !this.showEmail)
            parts.push(`<strong>${recipient.mobile}</strong>`)
          else
            parts.push(`<span>${recipient.mobile}</span>`)
        }
        return parts.join(', ')
      },
      onAdded(user) {
        if (this.users) {
          this.users.unshift(user)
          if (this.multiple)
            this.user_ids.unshift(user.id)
          else
            this.user_ids = user.id
        }
      },
      onOpen() {
        this.opened = true
      },
      onClose() {
        this.opened = false
        this.emitIfChanged()
      },
      onInput() {
        if (!this.opened) this.emitIfChanged()
      },
      onSelect() {
        document.querySelector('#users-treeselect input.vue-treeselect__input').value = ''
      },
      emitIfChanged() {
        if (this.multiple && !_.isEqual(_.sortBy(this.user_ids), _.sortBy(this.value)) ||
          !this.multiple && !_.isEqual(this.user_ids, this.value)) {
          this.$emit('input', this.user_ids)
        }
        let emails = []
        if (this.multiple) {
          if (this.user_ids && this.user_ids.length) {
            emails = _.reduce(this.users, (emails, user) => {
              if (this.user_ids.indexOf(user.id) >= 0) emails.push(user.email)
              return emails
            }, [])
          }
          if (!_.isEqual(emails, this.user_emails)) {
            this.user_emails = emails
            this.$emit('emails', emails)
          }
        } else {
          this.$emit('user', _.find(this.users, {id: this.user_ids}))
        }
      },
      removeDuplicates(users) {
        // Back-end might return duplicates - clean that up
        let ids = []
        return _.reduce(users, (users, user) => {
          if (ids.indexOf(user.id) === -1) {
            users.push(user)
            ids.push(user.id)
          }
          return users
        }, [])
      },
      load() {
        this.busy = false
        this.error = null
        let url = `/organization/${this.organization.id}/user/list?per_page=500`
        if (this.confirmed === true) url += '&confirmed=true'
        else if (this.confirmed === false) url += '&confirmed=false'
        if (this.permission) url += `&permission=${this.permission}`
        axios.get(url)
        .then(response => {
          this.busy = false
          this.users = this.removeDuplicates(response.data.data)
          this.$emit('all-users', this.users)
          if (this.multiple) {
            if (this.emails) {
              let user_ids = _.reduce(this.users, (ids, user) => {
                if (user.email && this.emails.indexOf(user.email) >= 0) ids.push(user.id)
                return ids
              }, [])
              if (!_.isEqual(_.sortBy(user_ids), _.sortBy(this.user_ids))) {
                console.log('load 1', user_ids, this.user_ids)
                this.user_ids = user_ids
                this.$emit('input', this.user_ids)
              }
            } else {
              // Cleanup invalid users (ones that could have been removed)
              let user_ids = _.reduce(this.users, (ids, user) => {
                if ((this.user_ids || []).indexOf(user.id) >= 0) ids.push(user.id)
                return ids
              }, [])
              if (!_.isEqual(_.sortBy(user_ids), _.sortBy(this.user_ids))) {
                console.log('load 2', user_ids, this.user_ids)
                this.user_ids = user_ids
                this.$emit('input', this.user_ids)
              }
            }
          } else {
            // Single select - user_ids is a number
            let user = _.find(this.users, {id: this.user_ids})
            this.$emit('user', user)
          }
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onClickAdd() {
        this.query = document.querySelector('#users-treeselect input.vue-treeselect__input').value
        setTimeout(() => {
          this.$bvModal.show('add-guest-modal')
        }, 50)
      },
      onKeyDown(e) {
        // This is the only way we found to catch a user hitting Enter
        // Allows us to open the modal and add a value based on what they typed
        if (e.keyCode != 13) return
        let el = document.querySelector('#users-treeselect input.vue-treeselect__input')
        if (!e.target.isSameNode(el)) return
        if (el.value) {
          e.stopImmediatePropagation()
          this.onClickAdd()
          return false
        }
      },
    },
    mounted() {
      window.usersSelect = this
      this.load()
      if (this.showAddGuest)
        document.addEventListener('keydown', this.onKeyDown, true)
    },
    destroyed() {
      if (this.showAddGuest)
        document.removeEventListener('keydown', this.onKeyDown, true)
    },
  }
</script>
